import React, { ChangeEvent, useEffect, useState } from "react";
import { Col, Row, Form, Button } from "react-bootstrap";

interface DateTimePickerProps {
  name: string;
  label: string;
  register: any;
  required?: boolean;
  requiredMessage?: string;
  error?: any;
  disabled?: boolean;
  value?: string;
  onChange?: (formattedDateTime: string) => void;
  resetTrigger?: any;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  name,
  label,
  register,
  required = false,
  requiredMessage = "Date & Time is required",
  error,
  disabled,
  value,
  onChange,
  resetTrigger,
}) => {
  const [localDateTime, setLocalDateTime] = useState<string>("");
  useEffect(() => {
    if (resetTrigger !== undefined) {
      setLocalDateTime("");
    }
  }, [resetTrigger]);
  /** Get current date-time for min attribute */
  const getMinDateTime = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offset).toISOString().slice(0, 16);
  };

  /** Convert datetime-local value to IST format */
  const convertToISTFormat = (dateTimeLocal: string) => {
    const date = new Date(dateTimeLocal);
    const istOffsetMs = 5.5 * 60 * 60 * 1000;
    const istDate = new Date(date.getTime() + istOffsetMs);

    const pad = (n: number, z = 2) => String(n).padStart(z, "0");

    return (
      `${istDate.getUTCFullYear()}-` +
      `${pad(istDate.getUTCMonth() + 1)}-` +
      `${pad(istDate.getUTCDate())}T` +
      `${pad(istDate.getUTCHours())}:` +
      `${pad(istDate.getUTCMinutes())}:` +
      `${pad(istDate.getUTCSeconds())}.` +
      `${pad(istDate.getUTCMilliseconds(), 6)}+05:30`
    );
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLocalDateTime(event.target.value);
  };

  const handleDoneClick = () => {
    if (!localDateTime) return;

    const formatted = convertToISTFormat(localDateTime);
    onChange?.(formatted);
  };

  return (
    <Form.Group as={Col} className="mb-3">
      <Row className="align-items-center">
        <Form.Label column md={4}>
          <span className="float-end">
            {label}
            {required && <span className="text-danger">*</span>} :
          </span>
        </Form.Label>

        <Col md={8}>
          <Form.Control
            type="datetime-local"
            min={getMinDateTime()}
            className={`form-control ${error ? "is-invalid" : ""}`}
            disabled={disabled}
            value={localDateTime}
            {...register(name, {
              required: required ? requiredMessage : false,
            })}
            onChange={handleInputChange}
          />

          <div className="d-flex justify-content-end mt-2">
            <Button
              size="sm"
              variant="success"
              onClick={handleDoneClick}
              disabled={!localDateTime || disabled}
            >
              Done
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={4}></Col>
        <Col md={8}>{error && <p className="m-0 err-msg">{error}</p>}</Col>
      </Row>
    </Form.Group>
  );
};

export default DateTimePicker;
